import { PieChartProps } from 'components/pie-chart';
import { CarouselButtons } from 'components/carousel-buttons';
import { useSwipe } from 'hooks/use-swipe.hook';
import { CommunitySolarSubtotal } from './pies/community-solar-subtotal';
import { RemainderOfYourBill } from './pies/remainder-of-your-bill';
import { Savings } from './pies/savings';
import { SolarFarmProduction } from './pies/solar-farm-production';
import { TotalDueToArcadia } from './pies/total-due-to-arcadia';
import { YourBillWithArcadia } from './pies/your-bill-with-arcadia';
import { YourBillWithoutArcadia } from './pies/your-bill-without-arcadia';

export type PieCarouselProps = {
  headlineSolarSavingsRate?: number | null;
  totalOtherUtilityChargeCents: number;
  totalOtherUtilityChargePercent: number;
  totalSolarChargeCents: number;
  totalSolarChargePercent: number;
  totalSolarSavingsCents: number;
  totalSolarSavingsPercent: number;
  totalWithoutArcadia: number;
  total: number;
  utilityNames: string;
};

export const PieCarousel: React.FC<PieCarouselProps> = props => {
  const numberOfPies = 6;

  const { index, handleTouchMove, handleTouchStart, setIndex } = useSwipe({
    numberOfElements: numberOfPies,
  });

  // round totalSolarSavingsPercent up to a value that is visible in the pie charts.
  let { totalSolarSavingsPercent, totalSolarChargePercent } = props;
  const minSavingsPercent = Math.min(1, totalSolarChargePercent);
  if (
    props.totalSolarSavingsCents > 0 &&
    totalSolarSavingsPercent < minSavingsPercent
  ) {
    totalSolarSavingsPercent = minSavingsPercent;
    // take the minSavingsPercent out of totalSolarChargePercent so the values still add to 100%
    totalSolarChargePercent -= minSavingsPercent;
  }

  const savings = {
    percentage: totalSolarSavingsPercent,
    total: props.totalSolarSavingsCents,
  };
  const solar = {
    percentage: totalSolarChargePercent,
    total: props.totalSolarChargeCents,
  };
  const utility = {
    percentage: props.totalOtherUtilityChargePercent,
    total: props.totalOtherUtilityChargeCents,
  };

  const options: PieChartProps['options'] = {
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    radius: 75,
  };

  return (
    <div onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
      {index === 0 && (
        <YourBillWithArcadia
          options={options}
          savings={savings}
          solar={solar}
          utility={utility}
          utilityNames={props.utilityNames}
          total={props.total}
        />
      )}
      {index === 1 && (
        <YourBillWithoutArcadia
          options={options}
          total={props.totalWithoutArcadia}
        />
      )}
      {index === 2 && (
        <TotalDueToArcadia
          options={options}
          savings={savings}
          solar={solar}
          utility={utility}
          total={props.total}
        />
      )}
      {index === 3 && (
        <SolarFarmProduction
          options={options}
          savings={savings}
          solar={solar}
          utility={utility}
          utilityNames={props.utilityNames}
          total={props.totalSolarChargeCents}
        />
      )}
      {index === 4 && (
        <Savings
          options={options}
          savings={savings}
          solar={solar}
          utility={utility}
          rate={props.headlineSolarSavingsRate}
          total={props.totalSolarSavingsCents}
        />
      )}
      {index === 5 && (
        <CommunitySolarSubtotal
          options={options}
          savings={savings}
          solar={solar}
          utility={utility}
          rate={props.headlineSolarSavingsRate}
          total={props.totalSolarChargeCents}
        />
      )}
      {index === 6 && (
        <RemainderOfYourBill
          options={options}
          savings={savings}
          solar={solar}
          utility={utility}
          total={props.totalOtherUtilityChargeCents}
        />
      )}
      <CarouselButtons
        index={index}
        itemName="pie"
        length={numberOfPies + 1}
        setIndex={setIndex}
        margin={{ top: '16px' }}
      />
    </div>
  );
};
